import * as THREE from "three";
import { Engine } from "../../game/engine";
import { AudioBed } from "../../game/audio";

/**
 * Echo — memory-recall (Simon-style), redesigned for 3D depth + engagement.
 * Game 06 of 12. (v2 — feel-pass sweep.)
 *
 * Scoring contract preserved: seed-driven sequence + per-round tap events
 * (i, seg, ms) replay unchanged. Visual layer rebuilt: four 3D pillars
 * around a center node, lit progressively during demo, glowing on correct
 * recall. Harmonic-flash surprise every 5 cleared rounds.
 */

export const SEGMENTS = 4;
export const MAX_DEPTH = 60;

export type Seg = 0 | 1 | 2 | 3;

export interface EchoTap { i: number; seg: number; ms: number }
export interface EchoRoundEvent { round: number; taps: EchoTap[] }

export interface EchoCallbacks {
  onHud: (round: number, phase: "demo" | "recall" | "over") => void;
  onGameOver: (result: { events: EchoRoundEvent[]; depth: number; scoreEstimate: number; reverseRoundsCleared: number; wrongTaps: { round: number; i: number; seg: number; ms: number }[]; livesUsed: number; roundAvgMs: number[] }) => void;
  /** v4 — fired when the player loses a life (wrong tap with lives remaining). */
  onLifeLost?: (livesRemaining: number, wrongSeg: number, expectedSeg: number) => void;
}

export interface EchoOptions {
  /** v5 — "Name that tune" accessibility mode. When true, the demo phase
   *  plays only audio (no segment flash), forcing the player to identify
   *  segments by ear. Recall-phase feedback (the player's own taps) is
   *  unchanged; this only blanks the demo. */
  audioOnly?: boolean;
  /** v5.1 — reduced-motion mode. When true, demo flashes shorten from
   *  320 ms / 500 ms gap to the dossier-spec 150 ms / 100 ms, the lift
   *  animation amplitude is dampened, and the demo cadence finishes
   *  faster so the player isn't held in motion for long. The mechanic
   *  is unchanged; only the visual envelope tightens. */
  reducedMotion?: boolean;
}

function makeRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function generateSequence(seed: number, length: number): number[] {
  const rng = makeRng(seed);
  const out: number[] = [];
  for (let i = 0; i < length; i++) out.push(Math.floor(rng() * SEGMENTS));
  return out;
}

// Pillar positions form a circle. Seg 0 = north, 1 = east, 2 = south, 3 = west.
const SEG_POSITIONS: [number, number][] = [
  [0, -6],  // north (negative Z)
  [6, 0],   // east
  [0, 6],   // south
  [-6, 0],  // west
];
// A-minor-7 arpeggio (A, C, E, G) in semitones from root. The dossier
// designates these as the per-segment chime notes; "Name that tune"
// audio-only mode relies on players learning seg ↔ pitch mapping.
const SEG_DETUNES = [0, 3, 7, 10];
const SEG_COLORS = [0x00e0ff, 0xffaa11, 0xff3df0, 0x66ff88];
const SEG_EMISSIVE = [0x0080cc, 0xff6600, 0xcc1aa0, 0x00aa44];

interface SegmentVisual {
  mesh: THREE.Mesh;
  mat: THREE.MeshStandardMaterial;
  haloMat: THREE.MeshBasicMaterial;
  halo: THREE.Mesh;
  flashUntil: number;
}

export class Echo {
  private engine: Engine;
  private audio = new AudioBed();
  private segments: SegmentVisual[] = [];
  private centerMesh: THREE.Mesh;
  private centerMat: THREE.MeshStandardMaterial;
  private seed: number;
  private fullSequence: number[];

  private phase: "idle" | "demo" | "recall" | "over" = "idle";
  private round = 1;
  private demoIdx = 0;
  private demoNextAt = 0;
  private recallTapIdx = 0;
  private recallStartedAt = 0;
  private events: EchoRoundEvent[] = [];
  private currentRoundTaps: EchoTap[] = [];
  // v4 — 3-life state. Each wrong tap costs a life; if any remain, the
  // player retries the round from the start. After 3 wrongs, run ends.
  private static readonly MAX_LIVES_CLIENT = 3;
  private wrongTaps: { round: number; i: number; seg: number; ms: number }[] = [];
  private cb: EchoCallbacks;
  private harmonicPulse = 0;
  private canvas: HTMLCanvasElement;
  private audioOnly = false;
  private reducedMotion = false;
  // Per-mode demo timings. Reduced motion targets the dossier-spec
  // 150 ms flash + 100 ms gap; default is the original 320 / 500.
  private flashMs = 320;
  private gapMs = 500;
  // v6 — three parallax starfield layers placed along the camera's
  // view ray (camera at (0,-16,14) looks at (0,2,0); view direction
  // is roughly (0, 0.79, -0.61)). Layers sit at increasing depth
  // along that ray so the dark indigo void reads as a concert-stage
  // sky with real depth, not a flat backdrop.
  // Per memory feedback-sibling-games-shallow §"real 3D scene with
  // depth cues". Mirrors the rebrand-pack pattern (Phalanx 2bc3ebc).
  private starLayers: THREE.Points[] = [];

  private onKey = (e: KeyboardEvent) => {
    if (this.phase !== "recall") return;
    let seg: Seg | null = null;
    if (e.code === "Digit1" || e.code === "Numpad1") seg = 0;
    else if (e.code === "Digit2" || e.code === "Numpad2") seg = 1;
    else if (e.code === "Digit3" || e.code === "Numpad3") seg = 2;
    else if (e.code === "Digit4" || e.code === "Numpad4") seg = 3;
    if (seg !== null) this.handleTap(seg);
  };
  private onPointer = (e: PointerEvent) => this.handlePointer(e);

  constructor(canvas: HTMLCanvasElement, cb: EchoCallbacks, seed = Math.floor(Math.random() * 0x7fffffff), opts: EchoOptions = {}) {
    this.cb = cb;
    this.seed = seed;
    this.canvas = canvas;
    this.audioOnly = opts.audioOnly === true;
    this.reducedMotion = opts.reducedMotion === true;
    if (this.reducedMotion) {
      this.flashMs = 150;
      this.gapMs = 100;
    }
    this.fullSequence = generateSequence(seed, MAX_DEPTH);

    this.engine = new Engine(canvas, { onFrame: (dt, t) => this.frame(dt, t) });
    this.engine.camera.position.set(0, -16, 14);
    this.engine.camera.lookAt(0, 2, 0);
    this.engine.camera.fov = 55;
    this.engine.camera.updateProjectionMatrix();
    this.engine.scene.fog = new THREE.Fog(0x040614, 14, 40);
    this.engine.scene.background = new THREE.Color(0x040614);

    const ambient = new THREE.AmbientLight(0x506080, 0.5);
    this.engine.scene.add(ambient);
    const key = new THREE.DirectionalLight(0xfff0d0, 0.8);
    key.position.set(6, 14, 8);
    this.engine.scene.add(key);

    // Floor disc.
    const floorGeo = new THREE.CircleGeometry(16, 64);
    const floorMat = new THREE.MeshStandardMaterial({ color: 0x040810, roughness: 0.9, metalness: 0.2 });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.5;
    this.engine.scene.add(floor);

    // Floor ring connecting pillar bases visually.
    const ringGeo = new THREE.RingGeometry(5.7, 6.3, 64);
    const ringMat = new THREE.MeshBasicMaterial({ color: 0x66ddff, transparent: true, opacity: 0.3, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = -0.48;
    this.engine.scene.add(ring);

    // Center node — illuminates as the recall accumulates correct taps.
    const cGeo = new THREE.SphereGeometry(0.9, 24, 18);
    this.centerMat = new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0x333355, emissiveIntensity: 0.5, roughness: 0.3, metalness: 0.5 });
    this.centerMesh = new THREE.Mesh(cGeo, this.centerMat);
    this.centerMesh.position.set(0, 0.5, 0);
    this.engine.scene.add(this.centerMesh);

    // Pillars at the 4 cardinal positions.
    for (let i = 0; i < SEGMENTS; i++) {
      const [x, z] = SEG_POSITIONS[i];
      const geo = new THREE.BoxGeometry(2.4, 2.0, 2.4);
      const mat = new THREE.MeshStandardMaterial({
        color: SEG_COLORS[i],
        emissive: SEG_EMISSIVE[i],
        emissiveIntensity: 0.5,
        roughness: 0.4,
        metalness: 0.5,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(x, 1, z);
      this.engine.scene.add(mesh);

      // Halo disc under each pillar.
      const haloGeo = new THREE.RingGeometry(1.6, 2.4, 32);
      const haloMat = new THREE.MeshBasicMaterial({ color: SEG_COLORS[i], transparent: true, opacity: 0.35, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
      const halo = new THREE.Mesh(haloGeo, haloMat);
      halo.rotation.x = -Math.PI / 2;
      halo.position.set(x, -0.4, z);
      this.engine.scene.add(halo);

      this.segments.push({ mesh, mat, haloMat, halo, flashUntil: 0 });
    }

    // v6 — parallax starfield. Three THREE.Points layers along the
    // camera's view ray. Each layer's center is placed at depth D
    // along (0, 0.79, -0.61) so the stars sit behind the play disc
    // in the camera's actual line of sight. Sizes shrink with distance
    // (sizeAttenuation:true), giving a near-vs-far parallax depth cue
    // without any per-frame cost. fog:false so stars persist past the
    // FogExp2 ramp (which ends at 40u; nearest layer is past that).
    const makeStars = (count: number, center: THREE.Vector3, radius: number, opts: { size: number; color: number }) => {
      const positions = new Float32Array(count * 3);
      // Seeded off the run seed XOR depth so each layer has its own
      // reproducible scatter (matches the seed → run philosophy).
      let s = (seed ^ Math.imul(((center.x + center.y + center.z) | 0), 0x9e3779b1)) | 0;
      const r = () => {
        s = (s + 0x6d2b79f5) | 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
      for (let i = 0; i < count; i++) {
        positions[i * 3] = center.x + (r() - 0.5) * radius * 2;
        positions[i * 3 + 1] = center.y + (r() - 0.5) * radius * 1.3;
        positions[i * 3 + 2] = center.z + (r() - 0.5) * radius * 0.7;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({
        color: opts.color, size: opts.size, sizeAttenuation: true,
        transparent: true, opacity: 0.85, depthWrite: false, fog: false,
      });
      const pts = new THREE.Points(geo, mat);
      this.engine.scene.add(pts);
      this.starLayers.push(pts);
    };
    // Centers placed along the camera's view ray at D = 200 / 100 / 50
    // from the origin (camera looks toward +Y, -Z). Cool-neutral palette
    // matches Echo's dark-indigo concert-stage mood (background 0x040614).
    makeStars(400, new THREE.Vector3(0, 158, -122), 220, { size: 0.55, color: 0x7080a0 }); // far
    makeStars(200, new THREE.Vector3(0, 79, -61),   130, { size: 0.48, color: 0x9098b8 }); // mid
    makeStars(80,  new THREE.Vector3(0, 40, -30),    75, { size: 0.45, color: 0xb0b8d8 }); // near

    window.addEventListener("keydown", this.onKey);
    canvas.addEventListener("pointerdown", this.onPointer);
  }

  private handlePointer(e: PointerEvent) {
    if (this.phase !== "recall") return;
    const rect = this.canvas.getBoundingClientRect();
    const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ny = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
    // Project onto y=0 plane (where pillars sit).
    const ndc = new THREE.Vector3(nx, ny, 0.5).unproject(this.engine.camera);
    const dir = ndc.sub(this.engine.camera.position).normalize();
    const dist = -this.engine.camera.position.y / dir.y;
    const wx = this.engine.camera.position.x + dir.x * dist;
    const wz = this.engine.camera.position.z + dir.z * dist;
    // Nearest segment by squared distance.
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < SEGMENTS; i++) {
      const [sx, sz] = SEG_POSITIONS[i];
      const d = (wx - sx) ** 2 + (wz - sz) ** 2;
      if (d < bestD) { bestD = d; best = i; }
    }
    this.handleTap(best as Seg);
  }

  private handleTap(seg: Seg) {
    if (this.phase !== "recall" || this.round > MAX_DEPTH) return;
    // v3: reverse rounds (every 7th cleared round) — expected segment
    // is the seeded sequence indexed FROM THE END for this round.
    const reverse = this.round >= 7 && this.round % 7 === 0;
    const expectedIdx = reverse ? this.round - 1 - this.recallTapIdx : this.recallTapIdx;
    const expected = this.fullSequence[expectedIdx];
    const ms = performance.now() - this.recallStartedAt;
    this.flashSegment(seg, 280);
    this.audio.chime(SEG_DETUNES[seg], 0);
    this.currentRoundTaps.push({ i: this.recallTapIdx, seg, ms });

    if (seg !== expected) {
      // v4: record this as a wrong tap. If lives remain, retry the round
      // from the start; otherwise the run ends.
      this.wrongTaps.push({ round: this.round, i: this.recallTapIdx, seg, ms });
      this.audio.thud();
      if (this.wrongTaps.length >= Echo.MAX_LIVES_CLIENT) {
        // Out of lives — record whatever progress was made + end.
        this.events.push({ round: this.round, taps: this.currentRoundTaps });
        this.endRun();
        return;
      }
      const livesRemaining = Echo.MAX_LIVES_CLIENT - this.wrongTaps.length;
      this.cb.onLifeLost?.(livesRemaining, seg, expected);
      // Retry: restart recall for this round with a fresh tap buffer.
      this.currentRoundTaps = [];
      this.recallTapIdx = 0;
      this.centerMat.emissiveIntensity = 0.4;
      this.centerMat.emissive.setHex(0x333355);
      // Brief "wrong" flash on the segment they actually pressed.
      this.flashSegment(seg, 600);
      return;
    }
    this.recallTapIdx += 1;
    // Center node brightens as recall progresses.
    const recallProgress = this.recallTapIdx / this.round;
    this.centerMat.emissiveIntensity = 0.5 + recallProgress * 2.0;
    this.centerMat.emissive.setHex(SEG_EMISSIVE[seg]);

    if (this.recallTapIdx >= this.round) {
      this.events.push({ round: this.round, taps: this.currentRoundTaps });
      this.currentRoundTaps = [];
      this.round += 1;
      // Harmonic-flash surprise every 5 cleared rounds.
      if ((this.round - 1) % 5 === 0) {
        this.harmonicPulse = 1;
        // Quick arpeggio.
        this.audio.chime(0, -0.5);
        this.audio.chime(7, 0);
        this.audio.chime(12, 0.5);
      }
      if (this.round > MAX_DEPTH) { this.endRun(); return; }
      this.startDemo();
    }
  }

  private startDemo() {
    this.phase = "demo";
    this.demoIdx = 0;
    // Reduced-motion shortens the lead-in too so the demo doesn't dwell.
    this.demoNextAt = performance.now() + (this.reducedMotion ? 250 : 600);
    this.recallTapIdx = 0;
    this.centerMat.emissiveIntensity = 0.4;
    this.centerMat.emissive.setHex(0x333355);
    this.cb.onHud(this.round, "demo");
  }

  private startRecall() {
    this.phase = "recall";
    this.recallStartedAt = performance.now();
    this.recallTapIdx = 0;
    this.currentRoundTaps = [];
    this.cb.onHud(this.round, "recall");
  }

  private flashSegment(seg: number, durMs: number) {
    this.segments[seg].flashUntil = performance.now() + durMs;
  }

  private endRun() {
    this.phase = "over";
    this.audio.setDroneIntensity(0);
    let depth = 0;
    let reverseRoundsCleared = 0;
    const roundAvgMs: number[] = [];
    for (const ev of this.events) {
      const isReverse = ev.round >= 7 && ev.round % 7 === 0;
      let allMatch = ev.taps.length === ev.round;
      if (allMatch) {
        for (let i = 0; i < ev.taps.length; i++) {
          const expectedIdx = isReverse ? ev.round - 1 - i : i;
          if (ev.taps[i].seg !== this.fullSequence[expectedIdx]) { allMatch = false; break; }
        }
      }
      if (allMatch) {
        depth = ev.round;
        if (isReverse) reverseRoundsCleared += 1;
        const sum = ev.taps.reduce((a, t) => a + t.ms, 0);
        roundAvgMs.push(ev.taps.length > 0 ? sum / ev.taps.length : 0);
      }
    }
    const livesUsed = this.wrongTaps.length;
    const livesRemaining = Math.max(0, Echo.MAX_LIVES_CLIENT - livesUsed);
    const lifeBonus = depth > 0 ? livesRemaining * 250 : 0;
    const scoreEstimate = depth * depth * 100 + reverseRoundsCleared * 250 + lifeBonus;
    this.cb.onHud(this.round, "over");
    this.cb.onGameOver({ events: this.events, depth, scoreEstimate, reverseRoundsCleared, wrongTaps: this.wrongTaps, livesUsed, roundAvgMs });
  }

  private frame(dt: number, _t: number) {
    // v6 — engagement signal: combines long-arc round progress with
    // within-round recall tension. Drives post-processing (engine
    // bloom/afterimage) and adaptive drone bed so each successful
    // round audibly thickens the void and each correct recall tap
    // tightens the moment. Per memory feedback-sibling-games-shallow
    // §"post-processing" + §"at least one escalation hook".
    const roundProgress = Math.min(1, (this.round - 1) / 20);
    const recallProgress = this.phase === "recall"
      ? Math.min(1, this.recallTapIdx / Math.max(1, this.round))
      : 0;
    const engagement = Math.min(1, roundProgress * 0.65 + recallProgress * 0.35);
    this.engine.setBoost(engagement, dt);
    try { this.audio.setDroneIntensity(0.15 + engagement * 0.55); } catch { /* off */ }

    if (this.phase === "demo") {
      const now = performance.now();
      if (now >= this.demoNextAt) {
        if (this.demoIdx < this.round) {
          const seg = this.fullSequence[this.demoIdx];
          // v5 audio-only: skip the visual flash during demo so the player
          // must identify the segment by chime alone. Chime always plays.
          if (!this.audioOnly) this.flashSegment(seg, this.flashMs);
          this.audio.chime(SEG_DETUNES[seg], 0);
          this.demoIdx += 1;
          this.demoNextAt = now + this.flashMs + this.gapMs;
        } else {
          this.startRecall();
        }
      }
    }

    // Per-frame: pillar flash + lift animations.
    const now = performance.now();
    for (let i = 0; i < SEGMENTS; i++) {
      const s = this.segments[i];
      const flashing = now < s.flashUntil;
      const flashAge = flashing ? 1 - (s.flashUntil - now) / this.flashMs : 1;
      const lift = flashing ? (1 - flashAge) * 1.4 : 0;
      s.mesh.position.y = 1 + lift + this.harmonicPulse * 0.6;
      s.mat.emissiveIntensity = flashing ? 1.5 + (1 - flashAge) * 1.5 : 0.5 + this.harmonicPulse * 1.0;
      s.haloMat.opacity = flashing ? 0.7 : 0.3 + this.harmonicPulse * 0.4;
    }
    if (this.harmonicPulse > 0) this.harmonicPulse = Math.max(0, this.harmonicPulse - dt * 1.6);

    // Center node breathing.
    this.centerMesh.rotation.y = now * 0.0005;
    this.centerMesh.scale.setScalar(1 + Math.sin(now * 0.003) * 0.04 + this.harmonicPulse * 0.3);
  }

  start() {
    this.audio.arm();
    // v6 — baseline drone. Per-frame frame() ramps from 0.15 with
    // engagement signal; this is just the floor while idle/demo.
    this.audio.setDroneIntensity(0.15);
    this.round = 1;
    this.events = [];
    this.currentRoundTaps = [];
    this.wrongTaps = [];
    this.harmonicPulse = 0;
    this.centerMat.emissiveIntensity = 0.4;
    this.centerMat.emissive.setHex(0x333355);
    this.startDemo();
    this.engine.start();
  }

  setMuted(b: boolean) { this.audio.setMuted(b); }
  setVolume(v: number) { this.audio.setVolume(v); }
  armAudio() { this.audio.arm(); }

  dispose() {
    // v6 — quiet the drone + drop parallax-starfield layers so a
    // navigate-away → re-mount cycle doesn't bleed audio or stack
    // THREE.Points on the scene graph.
    try { this.audio.setDroneIntensity(0); } catch { /* off */ }
    for (const layer of this.starLayers) {
      this.engine.scene.remove(layer);
      layer.geometry.dispose();
      (layer.material as THREE.Material).dispose();
    }
    this.starLayers = [];
    window.removeEventListener("keydown", this.onKey);
    this.canvas.removeEventListener("pointerdown", this.onPointer);
    this.engine.dispose();
  }
}
